import {
  RequestForQuotationLookupApiService,
  RequestForQuotationSupplierLookup,
} from '@abb-procure/data';
import { DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  booleanAttribute,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { QuicklinkDirective } from 'ngx-quicklink';
import { merge } from 'rxjs';
import { DatePipe } from '../../pipes/date.pipe';
import { NgChanges } from '../../utilities/angular-types.util';
import { PaginationUtility } from '../../utilities/pagination.util';
import { EntityBadge } from '../entity-badge/entity-badge.component';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { Paginator } from '../paginator/paginator.component';
import { StatusBadge } from '../status-badge/status-badge.component';

@Component({
  selector: 'procure-request-for-quotation-table',
  templateUrl: './request-for-quotation-table.component.html',
  styleUrl: './request-for-quotation-table.component.scss',
  imports: [
    LoadingIndicator,
    MatTable,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderCellDef,
    MatCell,
    MatRow,
    MatCellDef,
    MatColumnDef,
    MatNoDataRow,
    MatRowDef,
    MatHeaderRowDef,
    MatSort,
    EntityBadge,
    RouterLink,
    StatusBadge,
    Paginator,
    DecimalPipe,
    DatePipe,
    QuicklinkDirective,
  ],
})
export class RequestForQuotationTable implements OnInit, OnChanges {
  @Input() supplierId?: string | null;

  @Input({ transform: booleanAttribute }) protected openInNewTab = false;

  @Input()
  protected readonly displayedColumns: readonly string[] = [
    'entityNumber',
    'name',
    'status',
    'country',
    'dueDate',
    'projectSCM',
    'bu',
    'lui',
  ];

  protected readonly sort = viewChild.required(MatSort);
  protected readonly paginator = viewChild.required(Paginator);

  protected readonly dataSource =
    new MatTableDataSource<RequestForQuotationSupplierLookup>([]);

  protected dataSourceTotalLength: number = 0;
  protected readonly isLoading = signal(false);

  private readonly requestForQuotationApiService = inject(
    RequestForQuotationLookupApiService,
  );

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.fetchData();

    this.sort().disableClear = true;

    merge(this.sort().sortChange, this.paginator().page)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.fetchData();
      });
  }

  ngOnChanges(changes: NgChanges<RequestForQuotationTable>): void {
    if (
      'supplierId' in changes &&
      changes.supplierId?.previousValue !== changes.supplierId?.currentValue
    ) {
      this.fetchData();
    }
  }

  private async fetchData(): Promise<void> {
    const sort = this.sort();
    const paginator = this.paginator();

    if (this.isLoading() || !paginator || !sort || !this.supplierId) {
      return;
    }

    this.isLoading.set(true);

    const response =
      await this.requestForQuotationApiService.getRequestForQuotationsBySupplier(
        this.supplierId,
        PaginationUtility.getPaginationRequestInfo(paginator, sort),
      );

    this.isLoading.set(false);

    if (!response) {
      return;
    }

    this.dataSource.data = response.items;
    this.dataSourceTotalLength = response.totalCount;
    paginator.pageSize = response.pageSize;
    paginator.pageIndex = response.pageIndex - 1;
  }
}
